import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, isBoolean, isDate, isEmpty } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box } from '@mui/system';
import { Badge, IconButton } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import useValidationSchema from './_form';
import { IPatientSearchFormInner } from './_types';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import FormDatePicker from 'components/Form/Date/Date';
import FormSwitch from 'components/Form/Switch/Switch';
import { Papeer } from 'components/Papeer/Papeer';
import Button from 'components/Buttons/Button';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { InputType } from 'constants/constants';

const mainSx = { width: { xs: '100%', md: '50%', lg: '20%' } };
const smallScreenButtonWidth = { minWidth: { xs: 120, sm: 'auto' } };

export const PatientSearchFormInner: React.FC<IPatientSearchFormInner> = ({
  searchFieldOpened,
  setSearchFieldOpened,
  canSearch,
}) => {
  const { t } = useTranslation('Patients');
  const { compactMode } = useAppInfo();
  const { genderItems, formItems, extendedFields } = useValidationSchema(t);

  const { reset, watch } = useFormContext();
  const allFormValues = watch();
  const deceasedValue = watch('deceased');

  const [changeCount, setChangeCount] = useState<number>(0);

  const resetAll = () => {
    reset();
  };

  useEffect(() => {
    let changeCount = 0;
    extendedFields.forEach((item) => {
      const value = get(allFormValues, item);
      if (!isEmpty(value) || isDate(value) || (isBoolean(value) && value)) {
        ++changeCount;
      }
    });
    setChangeCount(changeCount);
  }, [allFormValues, extendedFields]);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          boxShadow: searchFieldOpened ? 4 : 'none',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Papeer
            bottomMargin={true}
            sx={{
              pt: 1,
              borderBottomRightRadius: searchFieldOpened ? 0 : null,
              borderBottomLeftRadius: searchFieldOpened ? 0 : null,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: {
                  xs: 'wrap',
                  lg: 'nowrap',
                },
                paddingRight: { xs: '0px', sm: '48px' },
                position: { xs: 'static', sm: 'relative' },
              }}
            >
              <Box sx={mainSx}>
                <FormInput
                  name="birthNumber"
                  label={t('birthNumber')}
                  InputProps={{ sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
                  inputProps={{ maxLength: 10 }}
                />
              </Box>

              <Box sx={mainSx}>
                <FormInput
                  name="lastName"
                  label={t('lastName')}
                  InputProps={{ sx: { borderRadius: 0 } }}
                />
              </Box>

              <Box sx={mainSx}>
                <FormInput
                  name="firstName"
                  label={t('firstName')}
                  InputProps={{ sx: { borderRadius: 0 } }}
                />
              </Box>

              <Box sx={mainSx}>
                <FormInput name="city" label={t('city')} InputProps={{ sx: { borderRadius: 0 } }} />
              </Box>

              <Box sx={mainSx}>
                <FormSelect
                  name="gender"
                  label={t('gender')}
                  items={genderItems}
                  sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  clearable={true}
                />
              </Box>
            </Box>

            <Box
              sx={{
                position: { sm: 'absolute' },
                right: 0,
                top: 8,
                textAlign: { xs: 'center', sm: 'right' },
              }}
            >
              <IconButton
                aria-label="expand"
                onClick={() => setSearchFieldOpened(!searchFieldOpened)}
              >
                <Badge badgeContent={changeCount} color="primary">
                  {searchFieldOpened ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </Badge>
              </IconButton>
            </Box>

            <Box
              display={'flex'}
              flexDirection={'row'}
              flexWrap={'nowrap'}
              justifyContent={'flex-end'}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                }}
              >
                <Button
                  sx={smallScreenButtonWidth}
                  variant="contained"
                  color="inherit"
                  onClick={() => resetAll()}
                >
                  {t('reset')}
                </Button>
                <Button
                  sx={smallScreenButtonWidth}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!canSearch}
                  className="search-form-button"
                >
                  {t('search')}
                </Button>
              </Box>
            </Box>
          </Papeer>
        </Box>
        <Box sx={searchFieldOpened ? undefined : { display: 'none' }}>
          <Box
            sx={{
              position: 'absolute',
              background: (theme) => theme.palette.background.paper,
              zIndex: 1030,
              p: compactMode ? 1 : 2,
              pt: 0,
              left: 0,
              right: 0,
              top: '100%',
              boxShadow: 4,
              borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
              borderBottomRightRadius: (theme) => theme.shape.borderRadius,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: {
                  xs: 'wrap',
                },
              }}
            >
              {formItems.map((formItem) => {
                const type = get(formItem, 'type', 'input');
                const name = get(formItem, 'name', '');
                const label = get(formItem, 'label', '');
                return (
                  <Box
                    key={`formItem-${name}`}
                    sx={{
                      width: { xs: '100%', md: '50%', lg: 1 / 5, xl: 1 / 7 },
                      pr: { lg: 1 },
                    }}
                  >
                    {type === 'select' ? <FormSelect name={name} label={label} items={[]} /> : null}
                    {type === 'switch' ? <FormSwitch name={name} label={label} /> : null}
                    {type === 'input' ? (
                      <FormInput name={name} label={label} type={type as InputType} />
                    ) : null}
                    {type === 'date' ? <FormDatePicker name={name} label={label} /> : null}
                  </Box>
                );
              })}

              {/* Deceased date fields - only shown when deceased is true */}
              <Box
                sx={{
                  width: { xs: '100%', md: '50%', lg: 1 / 5, xl: 1 / 7 },
                  pr: { lg: 1 },
                }}
              >
                <FormDatePicker
                  name="deceasedDateFrom"
                  label={t('deceasedDateFrom')}
                  disabled={!deceasedValue}
                />
              </Box>
              <Box
                sx={{
                  width: { xs: '100%', md: '50%', lg: 1 / 5, xl: 1 / 7 },
                  pr: { lg: 1 },
                }}
              >
                <FormDatePicker
                  name="deceasedDateTo"
                  label={t('deceasedDateTo')}
                  disabled={!deceasedValue}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PatientSearchFormInner;
